import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../api";
import Topbar from "./Topbar";
import { moneyK, moneyMeaningTitle } from "./ui";
// The condition row lives in ./Conditions so the automations' own filters render
// the identical editor. See the header there for why one copy matters.
import { BLANK, ConditionRow, TWO_VALUES, coerce, decode } from "./Conditions";

/**
 * THE RULE BUILDER — where a queue gets argued with.
 *
 * Implemented against Gabriel's Claude Design mockup (Follow-ups.dc.html, the `isBuilder`
 * screen). Four cards on the left — name, conditions, score, cap and cadence — and a
 * sticky panel on the right that answers the only question that matters while you type:
 * how many companies does this actually match, and which ones.
 *
 * THE VOCABULARY COMES FROM THE SERVER. Fields, their types, which operators each type
 * accepts, the score factors and the cadences all arrive from GET /api/follow-up-rules.
 * A builder holding its own copy of that list can offer a field the engine does not read,
 * and the rule then saves cleanly and matches nothing — which is the exact failure mode
 * ("a queue that reads as no work to do") the whole rebuild exists to remove.
 *
 * THE PREVIEW RUNS THE REAL ENGINE. POST /follow-up-rules/preview takes the unsaved draft
 * and evaluates it over the real facts with suppression applied, so the number on screen
 * is the number you get on save. Debounced, because it posts as you type.
 */

const CADENCE_LABELS = {
  // Honest labels. The mockup offers "Every weekday, 7:00" and "1st of the month", and
  // neither exists: there is no scheduled job, and the month is not a cadence the engine
  // knows. Naming a schedule the system does not keep is how a page starts lying.
  always: "Every day",
  mondays: "Mondays and Tuesdays",
  weekly: "Once a week",
};

/* The database keys say what they mean to the schema; these say what they mean to
   Gabriel, in the words the rest of the CRM uses for the same statuses. */
const PROPOSE_LABELS = {
  lost: "Mark it lost",
  not_a_fit: "Mark it not a fit",
  recycled: "Park it for later",
};

const CADENCE_HINTS = {
  always: "Renders whenever you open the page.",
  mondays: "A start-of-week job. Quiet Wednesday to Sunday.",
  weekly: "Opens once, stays up for that day, then goes quiet for seven.",
};

/** The headline of a field's doc: everything up to the first full stop. */
function firstSentence(doc) {
  const text = String(doc || "");
  const stop = text.search(/\.\s/);
  return stop === -1 ? text : `${text.slice(0, stop)}.`;
}

// -------------------------------------------------------------------- page

export default function RuleBuilderPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === "new";

  const [vocabulary, setVocabulary] = useState(null);
  const [rule, setRule] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);

  // Draft state. Held flat rather than as the nested conditions JSON, because the form is
  // a list of rows and the tree only has to exist at save time.
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [combiner, setCombiner] = useState("all");
  const [conds, setConds] = useState([BLANK]);
  const [base, setBase] = useState(20);
  const [multipliers, setMultipliers] = useState({});
  const [cap, setCap] = useState("");
  const [cadence, setCadence] = useState("always");
  const [chipLabel, setChipLabel] = useState("");
  // The "then". actionConfig is a loose bag rather than typed state because its
  // shape belongs to the chosen action, and the server is what validates the pair.
  const [action, setAction] = useState("show");
  const [actionConfig, setActionConfig] = useState({});
  const [actionCap, setActionCap] = useState("");

  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const debounce = useRef(null);

  // --- load --------------------------------------------------------------
  useEffect(() => {
    (async () => {
      try {
        const data = await api("/follow-up-rules");
        setVocabulary(data.vocabulary);

        const found = isNew ? null : data.rules.find((r) => String(r.id) === String(id));
        if (!isNew && !found) {
          setError("That rule no longer exists.");
          return;
        }
        setRule(found);

        const defaults = Object.fromEntries(data.vocabulary.factors.map((f) => [f.name, f.default]));
        if (!found) {
          setMultipliers(defaults);
          return;
        }

        setName(found.name || "");
        setDescription(found.description || "");
        setEnabled(found.enabled !== false);
        setCap(found.cap == null ? "" : String(found.cap));
        setCadence(found.cadence || "always");
        setChipLabel(found.chip_label || "");
        setAction(found.action || "show");
        setActionConfig(found.action_config || {});
        setActionCap(found.action_daily_cap == null ? "" : String(found.action_daily_cap));
        setBase(Number((found.score || {}).base) || 0);
        setMultipliers({ ...defaults, ...((found.score || {}).multipliers || {}) });

        const tree = found.conditions || {};
        const which = tree.all ? "all" : "any";
        setCombiner(which);
        // Nested groups are flattened for editing. Two levels is the DSL's limit and the
        // form is a flat list, so a rule hand-written with a nested group loads as its
        // leaves — and the note under the toggle says so rather than silently rewriting it.
        const leaves = [];
        for (const node of tree[which] || []) {
          if (node && (node.all || node.any)) leaves.push(...(node.all || node.any));
          else leaves.push(node);
        }
        setConds(leaves.length ? leaves : [BLANK]);
      } catch (err) {
        setError(err.message);
      }
    })();
  }, [id, isNew]);

  // --- the draft, in the shape the API takes -----------------------------
  const draft = useMemo(() => {
    const fields = (vocabulary && vocabulary.fields) || [];
    const usable = conds.filter((c) => c.field);
    return {
      name: name.trim(),
      description: description.trim() || null,
      enabled,
      chip_label: chipLabel.trim() || null,
      cap: cap === "" ? null : Number(cap),
      cadence,
      action,
      action_config: actionConfig,
      action_daily_cap: actionCap === "" ? null : Number(actionCap),
      conditions: {
        [combiner]: usable.map((c) => {
          const field = fields.find((f) => f.name === c.field) || null;
          const decoded = decode(c.value, c.op);
          const leaf = { field: c.field, op: c.op };
          const value = coerce(
            field,
            c.op,
            // A range arrives as [from, to] and is read off the decoded halves; every
            // other shape (including the enum checkbox list) goes through as it is.
            TWO_VALUES.has(c.op) ? decoded.a : c.value,
            decoded.b
          );
          if (value !== undefined) leaf.value = value;
          return leaf;
        }),
      },
      score: { base: Number(base) || 0, multipliers },
    };
  }, [name, description, enabled, chipLabel, cap, cadence, action, actionConfig, actionCap,
      combiner, conds, base, multipliers, vocabulary]);

  // --- live preview ------------------------------------------------------
  // Debounced because it posts on every keystroke and runs the engine over every
  // in-scope company. 350ms is long enough that typing a number does not fire five times.
  useEffect(() => {
    if (!vocabulary) return;
    if (!draft.conditions[combiner].length) {
      setPreview(null);
      return;
    }
    clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      setPreviewing(true);
      try {
        setPreview(await api("/follow-up-rules/preview", { method: "POST", body: JSON.stringify(draft) }));
      } catch (err) {
        setPreview({ errors: [err.message], count: 0, sample: [] });
      } finally {
        setPreviewing(false);
      }
    }, 350);
    return () => clearTimeout(debounce.current);
  }, [draft, combiner, vocabulary]);

  // --- save --------------------------------------------------------------
  const save = useCallback(async () => {
    setSaving(true);
    setErrors([]);
    try {
      if (isNew) await api("/follow-up-rules", { method: "POST", body: JSON.stringify(draft) });
      else await api(`/follow-up-rules/${id}`, { method: "PATCH", body: JSON.stringify(draft) });
      navigate("/follow-ups");
    } catch (err) {
      // The API validates against the same engine the preview ran, so a rejection here is
      // a real disagreement worth reading rather than a form-level nag.
      setErrors([err.message]);
    } finally {
      setSaving(false);
    }
  }, [draft, id, isNew, navigate]);

  const remove = useCallback(async () => {
    if (!window.confirm(`Delete "${name}"? The queue disappears from Follow-ups.`)) return;
    setSaving(true);
    try {
      await api(`/follow-up-rules/${id}`, { method: "DELETE" });
      navigate("/follow-ups");
    } catch (err) {
      setErrors([err.message]);
      setSaving(false);
    }
  }, [id, name, navigate]);

  if (error) {
    return (
      <>
        <Topbar title="Rule builder" />
        <div className="crm-empty">{error}</div>
      </>
    );
  }
  if (!vocabulary) {
    return (
      <>
        <Topbar title="Rule builder" />
        <div className="crm-empty">Loading the fields…</div>
      </>
    );
  }

  const capN = cap === "" ? null : Number(cap);
  // Whether the chosen action does anything to a record. Read off the server's
  // vocabulary rather than compared against a string here, so adding an action
  // never needs this page edited to know it acts.
  const acting = !!((vocabulary && vocabulary.actions) || []).find((a) => a.name === action && a.acts);
  const matched = preview ? preview.count : null;
  const previewErrors = (preview && preview.errors) || [];
  const history = rule && rule.history;

  /*
   * "At 5 a day this queue clears its current 42 in 9 working days." The mockup's line,
   * and the honest version of the dry-run panel: a projection off the numbers actually on
   * screen rather than a replay of history the facts query cannot reconstruct. Working
   * days, not calendar days, because that is how the queue gets worked.
   */
  const clearsIn = capN && matched ? Math.ceil(matched / capN) : null;

  return (
    <>
      <Topbar
        title={isNew ? "New rule" : "Rule builder"}
        sub={
          isNew
            ? "A new queue on Follow-ups. Nothing is saved until you say so."
            : `Editing “${rule ? rule.name : ""}” · changes apply the next time you open Follow-ups`
        }
      >
        <Link className="crm-btn crm-btn--secondary" to="/follow-ups">
          Back to queues
        </Link>
      </Topbar>

      <div className="crm-rb">
        <div className="crm-rb__main">
          {/* --- name --- */}
          <div className="crm-rb__card">
            <div className="crm-rb__label">Rule name</div>
            <input className="crm-rb__name" value={name} onChange={(e) => setName(e.target.value)} placeholder="What is this queue for?" />
            <div className="crm-rb__hint">
              Shown as the section title, and in “why this?” on every row it pulls in.
            </div>

            <div className="crm-rb__pair">
              <label>
                <span className="crm-rb__label">One-line description</span>
                <input
                  className="crm-input"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Optional. Sits under the section title."
                />
              </label>
              <label>
                <span className="crm-rb__label">Row chip</span>
                <input
                  className="crm-input"
                  value={chipLabel}
                  onChange={(e) => setChipLabel(e.target.value)}
                  placeholder="e.g. no chase yet"
                />
              </label>
            </div>

            <label className="crm-rb__check">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              <span>
                Enabled — a disabled rule keeps its count in the rail but renders no rows, so you can
                see what turning it on would surface.
              </span>
            </label>
          </div>

          {/* --- conditions --- */}
          <div className="crm-rb__card">
            <div className="crm-rb__cardhead">
              <span className="crm-rb__title">Surface a company when</span>
              <div className="crm-pillradio crm-rb__combiner">
                {["all", "any"].map((c) => (
                  <button key={c} type="button" className={combiner === c ? "sel" : ""} onClick={() => setCombiner(c)}>
                    {c}
                  </button>
                ))}
              </div>
            </div>

            <div className="crm-rb__conds">
              {conds.map((cond, i) => (
                <ConditionRow
                  key={i}
                  index={i}
                  cond={cond}
                  fields={vocabulary.fields}
                  combiner={combiner === "all" ? "and" : "or"}
                  onChange={(idx, next) => setConds((cs) => cs.map((c, j) => (j === idx ? next : c)))}
                  onRemove={(idx) => setConds((cs) => (cs.length === 1 ? [BLANK] : cs.filter((_, j) => j !== idx)))}
                />
              ))}
            </div>

            <button type="button" className="crm-rb__add" onClick={() => setConds((cs) => [...cs, BLANK])}>
              + add condition
            </button>

            {!!previewErrors.length && (
              <div className="crm-rb__err">
                {previewErrors.join(" ")}
              </div>
            )}

            {/* The field the condition is written against, spelled out. The vocabulary's
                doc text is the difference between "days quiet" meaning what you think it
                means and a rule that looks right and is wrong. */}
            {conds.some((c) => c.field) && (
              <div className="crm-rb__docs">
                {conds
                  .filter((c) => c.field)
                  .map((c) => vocabulary.fields.find((f) => f.name === c.field))
                  .filter((f, i, all) => f && all.indexOf(f) === i)
                  .map((f) => (
                    /*
                      FIRST SENTENCE ONLY. These docs were written for someone reading
                      actions/rules.js, and days_quiet's runs to a paragraph about
                      negative clamping — accurate, and it swamped the card it was meant
                      to clarify. The rest stays one hover away rather than gone.
                    */
                    <div key={f.name} title={f.doc}>
                      <b>{f.label}</b> — {firstSentence(f.doc)}
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* --- score + cap --- */}
          <div className="crm-rb__row2">
            <div className="crm-rb__card">
              <div className="crm-rb__title">Order by score</div>
              <div className="crm-rb__hint">Higher score is what survives the cap.</div>

              <div className="crm-rb__base">
                <span>This queue's own weight</span>
                <input
                  className="crm-input crm-rb__basein"
                  value={base}
                  onChange={(e) => setBase(e.target.value.replace(/[^0-9]/g, ""))}
                />
              </div>
              <div className="crm-rb__hint">
                What makes scores comparable BETWEEN queues — a quiet deal outranks a cold lead
                because its rule weighs more, not because of anything on the row.
              </div>

              <div className="crm-rb__sliders">
                {vocabulary.factors.map((f) => (
                  <div key={f.name} className="crm-rb__slider">
                    <div className="crm-rb__sliderhead">
                      <span title={f.doc}>{f.label}</span>
                      <span className="crm-num">
                        {multipliers[f.name] === 0 ? "off" : `×${Number(multipliers[f.name] ?? f.default).toFixed(1)}`}
                      </span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max={vocabulary.multiplier_max}
                      step="0.1"
                      value={multipliers[f.name] ?? f.default}
                      onChange={(e) =>
                        setMultipliers((m) => ({ ...m, [f.name]: Number(e.target.value) }))
                      }
                    />
                  </div>
                ))}
              </div>
              <div className="crm-rb__hint">
                Zero switches a factor off. Most rules care about two or three and want the rest
                silent rather than quietly contributing.
              </div>
            </div>

            {/*
              THE "THEN". A rule matched some companies; this is what happens to
              them. 'Show it in the queue' is the default and does nothing to any
              record, which is what every rule written before this card did.

              The whole vocabulary arrives from the server (GET /follow-up-rules),
              including whether each action `acts` — so this page cannot offer an
              action the runner has no code for, and the "needs a daily limit" rule
              is stated once, in the engine, rather than guessed at here.
            */}
            <div className="crm-rb__card">
              <div className="crm-rb__title">Then</div>
              <div className="crm-rb__hint">What happens to a company this rule matches.</div>

              <div className="crm-pillradio">
                {(vocabulary.actions || []).map((a) => (
                  <button
                    key={a.name}
                    type="button"
                    className={action === a.name ? "sel" : ""}
                    onClick={() => setAction(a.name)}
                  >
                    {a.label}
                  </button>
                ))}
              </div>

              {!acting && (
                <div className="crm-rb__hint">
                  Nothing is changed and nothing is sent. The rule draws a queue and you decide
                  what to do with each row.
                </div>
              )}

              {acting && action === "propose_change" && (
                <>
                  <div className="crm-rb__label crm-rb__spaced">Propose</div>
                  <select
                    className="crm-select"
                    value={actionConfig.to || ""}
                    onChange={(e) =>
                      setActionConfig({
                        ...actionConfig,
                        change_type: "company_status",
                        to: e.target.value,
                      })
                    }
                  >
                    <option value="">Choose a change…</option>
                    {((vocabulary.proposable || []).find((p) => p.name === "company_status") || { to: [] }).to.map(
                      (t) => (
                        <option key={t} value={t}>
                          {PROPOSE_LABELS[t] || t}
                        </option>
                      )
                    )}
                  </select>
                  <div className="crm-rb__hint">
                    It lands in Approvals for you to accept or dismiss. Nothing moves on its own,
                    and a rule can never propose making somebody a client — that is what a signed
                    agreement is for.
                  </div>

                  {actionConfig.to === "recycled" && (
                    <div className="crm-rb__base crm-rb__spaced">
                      <span>Park for</span>
                      <input
                        className="crm-input crm-rb__basein"
                        value={actionConfig.recycle_months || ""}
                        onChange={(e) =>
                          setActionConfig({
                            ...actionConfig,
                            recycle_months: e.target.value.replace(/[^0-9]/g, ""),
                          })
                        }
                      />
                      <span>months</span>
                    </div>
                  )}

                  <div className="crm-rb__label crm-rb__spaced">Reason</div>
                  <input
                    className="crm-input"
                    value={actionConfig.status_reason || ""}
                    placeholder="ghosted"
                    onChange={(e) => setActionConfig({ ...actionConfig, status_reason: e.target.value })}
                  />
                </>
              )}

              {acting && (
                <>
                  <div className="crm-rb__base crm-rb__spaced">
                    <span>At most</span>
                    <input
                      className="crm-input crm-rb__basein"
                      value={actionCap}
                      placeholder="5"
                      onChange={(e) => setActionCap(e.target.value.replace(/[^0-9]/g, ""))}
                    />
                    <span>companies a day</span>
                  </div>
                  {/* Not the same number as the cap below, and the page has to say so
                      or the two read as duplicates of each other. */}
                  <div className="crm-rb__hint">
                    Required. This limits what the rule DOES; the cap below limits how many rows
                    you are shown. It runs once a day, and it never acts on the same company
                    twice.
                    {matched != null && actionCap !== "" && Number(actionCap) < matched ? (
                      <>
                        {" "}
                        <b>
                          {matched} match today, so it would work through them {Number(actionCap)} a
                          day.
                        </b>
                      </>
                    ) : null}
                  </div>
                </>
              )}
            </div>

            <div className="crm-rb__card">
              <div className="crm-rb__title">Cap and cadence</div>
              <div className="crm-rb__hint">The cap is what makes the section finishable.</div>

              <div className="crm-rb__base">
                <span>Show at most</span>
                <input
                  className="crm-input crm-rb__basein"
                  value={cap}
                  placeholder="all"
                  onChange={(e) => setCap(e.target.value.replace(/[^0-9]/g, ""))}
                />
                <span>a day</span>
              </div>
              <div className="crm-rb__hint">
                A capped queue picks its rows once a day and holds them; working one does not pull
                the next in until tomorrow. Leave it empty and the queue shows everything it matches.
              </div>

              <div className="crm-rb__label crm-rb__spaced">Refresh</div>
              <div className="crm-pillradio">
                {vocabulary.cadences.map((c) => (
                  <button key={c} type="button" className={cadence === c ? "sel" : ""} onClick={() => setCadence(c)}>
                    {CADENCE_LABELS[c] || c}
                  </button>
                ))}
              </div>
              <div className="crm-rb__hint">{CADENCE_HINTS[cadence]}</div>

              {clearsIn != null && (
                <div className="crm-rb__projection">
                  At {capN} a day this queue clears its current {matched} in{" "}
                  <b>
                    {clearsIn} working day{clearsIn === 1 ? "" : "s"}
                  </b>
                  , assuming no new matches.
                </div>
              )}
            </div>
          </div>

          {/* --- save --- */}
          {!!errors.length && <div className="crm-rb__err">{errors.join(" ")}</div>}

          <div className="crm-rb__foot">
            <button
              type="button"
              className="crm-btn crm-btn--primary"
              onClick={save}
              disabled={saving || !name.trim() || !draft.conditions[combiner].length || !!previewErrors.length}
            >
              {isNew ? "Create rule" : "Save rule"}
            </button>
            <Link className="crm-btn crm-btn--secondary" to="/follow-ups">
              Cancel
            </Link>

            <span className="crm-rb__prov">
              {isNew ? (
                "Nothing is written until you create it."
              ) : (
                <>
                  {rule && rule.is_seed ? "Shipped rule" : "Your rule"}
                  {rule && rule.created_at
                    ? ` · created ${new Date(rule.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
                    : ""}
                  {history
                    ? ` · surfaced ${history.surfaced}, worked ${history.worked}`
                    : capN
                      ? " · never surfaced yet"
                      : " · uncapped, so there is no daily batch to count"}
                </>
              )}
            </span>

            {/* Seeds are deletable too. A queue Gabriel cannot remove is one he works
                around, and working around this page is the failure it was rebuilt for. */}
            {!isNew && (
              <button type="button" className="crm-rb__delete" onClick={remove} disabled={saving}>
                Delete rule
              </button>
            )}
          </div>
        </div>

        {/* --- the live count --- */}
        <aside className="crm-rb__side">
          <div className="crm-rb__panel">
            <div className="crm-rb__panelhead">
              <div className="crm-rb__panellabel">Matches right now</div>
              {/* No number until there is one. An em-dash at 44px reads as a solid bar,
                  and a big fat placeholder where a count goes looks like a broken count
                  rather than an empty form. */}
              {matched != null && (
                <div className="crm-rb__big">
                  <span className="crm-num">{matched}</span>
                  <span>companies</span>
                </div>
              )}
              <div className="crm-rb__panelsub">
                {previewing
                  ? "checking…"
                  : matched == null
                    ? "Add a condition to see who this pulls in."
                    : capN
                      ? `${capN} a day surfaces the top ${Math.min(capN, matched)} tomorrow`
                      : "Uncapped — all of them, every day"}
              </div>
            </div>

            <div className="crm-rb__panelbody">
              <div className="crm-rb__label">Sample matches</div>
              {preview && preview.sample.length ? (
                <div className="crm-rb__samples">
                  {preview.sample.map((s) => (
                    <div key={s.company_id} className="crm-rb__sample">
                      <span className="crm-rb__samplename">{s.company_name}</span>
                      <span className="crm-rb__samplemeta crm-num" title={s.monthly_value ? moneyMeaningTitle(s.status) : undefined}>
                        {[
                          s.days_quiet == null ? null : `${s.days_quiet}d`,
                          s.monthly_value ? moneyK(s.monthly_value, s.currency) : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                      <span className="crm-rb__samplescore crm-num">{s.score}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="crm-rb__hint">
                  {matched === 0 ? "Nothing matches this yet." : "No sample yet."}
                </div>
              )}

              {preview && (
                <div className="crm-rb__panelfoot">
                  Ranked by the same engine the page runs, over {preview.in_scope} companies in
                  scope. Muted companies are already left out. Updates as you edit.
                </div>
              )}
            </div>
          </div>
        </aside>
      </div>
    </>
  );
}
